/**
 * OutputVerification.jsx — Subtab VII
 */

import React from "react";
import { HallucinationGuard } from "../diagrams.jsx";
import { SubtabHeading, GapBlock, SolutionLead, Body, P } from "../SubtabFrame.jsx";

export default function OutputVerification() {
  return (
    <article>
      <SubtabHeading roman="VII">Output Verification and Hallucination</SubtabHeading>
      <HallucinationGuard />
      <GapBlock>
        Air-gap doesn't fix bad outputs. The Mata v. Avianca pattern —
        fabricated citations, wrong analysis — persists regardless of
        deployment model, and Op. 512 plus Rule 3.3 impose real verification
        duties.
      </GapBlock>
      <SolutionLead>
        Architectural verification scaffolding integrated into the inference
        pipeline, plus mandatory supervising-attorney review protocols.
      </SolutionLead>
      <Body>
        <P>
          Citation verification is automated as a pre-output filter. Every
          legal citation in a generated output is checked against an
          authoritative source — Fastcase or vLex datasets cached on-prem, or
          Westlaw/Lexis through controlled diode ingest — before the output is
          returned to the user. Citations that cannot be verified are flagged
          inline as "UNVERIFIED — DO NOT CITE" and the system requires
          explicit acknowledgment from the user before the output is finalized.
        </P>
        <P>
          Retrieval-Augmented Generation (RAG) is the default architecture for
          any factual or legal-research query. The model is constrained to
          draw from a curated, firm-controlled document corpus (case law,
          statutes, regulations, internal precedent files, matter documents)
          and is prompted to cite source documents for every factual claim.
          Pure parametric generation (the model "knowing" things from
          training) is reserved for drafting and analysis tasks, not factual
          retrieval.
        </P>
        <P>
          The supervising attorney protocol — required by Rule 5.1 supervision
          and Op. 512 — defines a tiered review structure: high-stakes outputs
          (court filings, opinion letters, regulatory submissions) require
          supervising attorney sign-off before delivery to the engaging firm;
          medium-stakes outputs (research memoranda, deposition prep) require
          sampled review per defined cadence; low-stakes outputs (document
          summaries, calendar drafts) operate under standing review protocols.
          The cadence and tiering are documented per-matter in the engagement
          letter.
        </P>
        <P>
          Model output is also subject to confidence calibration — the system
          surfaces uncertainty signals (low-probability tokens, retrieval
          confidence scores) so the reviewing lawyer knows where to focus
          verification effort. This is closer to how radiologists use
          AI-assisted imaging review than how typical chatbot users interact
          with LLMs, and it's the right standard for legal work.
        </P>
      </Body>
    </article>
  );
}
